import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import {
  PermissionCheckState,
  PermissionCount,
  PermissionModuleNode,
  PermissionTreeNode,
} from '@app/features/dashboard/group-register/permission-tree/permission-tree.models';
import {
  TextSegment,
  checkState,
  countSelected,
  hasPendingChange,
  splitHighlight,
} from '@app/features/dashboard/group-register/permission-tree/permission-selection.utils';

/**
 * Árvore Módulo › Área › Tela da tela de grupo de permissão (card 3902), com contagem marcadas/total em todo nível
 * e marcar/desmarcar por nível (estado parcial quando só parte está marcada).
 *
 * O quadrado de marcar é um `button` com `role="checkbox"` e `aria-checked="mixed"`: o `app-checkbox` do Design
 * System não tem estado parcial, e a árvore precisa dele.
 */
@Component({
  selector: 'app-permission-tree',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './permission-tree.component.html',
  styleUrls: ['./permission-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionTreeComponent {
  readonly modules = input.required<PermissionModuleNode[]>();
  readonly selected = input.required<ReadonlySet<string>>();
  /** Seleção salva: nó com diferença ganha a bolinha âmbar. */
  readonly initial = input<ReadonlySet<string>>(new Set());
  /** Termo da busca, destacado no rótulo. Com busca, a contagem é de resultados. */
  readonly query = input('');
  readonly activeId = input<string | null>(null);
  readonly expanded = input<ReadonlySet<string>>(new Set());
  /** Na busca a árvore abre todos os ramos que têm resultado. */
  readonly expandAll = input(false);

  readonly nodeSelect = output<PermissionTreeNode>();
  readonly nodeToggle = output<PermissionTreeNode>();
  readonly expandToggle = output<string>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  isExpanded(id: string): boolean {
    return this.expandAll() || this.expanded().has(id);
  }

  count(node: PermissionTreeNode): PermissionCount {
    return countSelected(node.values, this.selected());
  }

  state(node: PermissionTreeNode): PermissionCheckState {
    return checkState(node.values, this.selected());
  }

  isChanged(node: PermissionTreeNode): boolean {
    return hasPendingChange(node.values, this.selected(), this.initial());
  }

  segments(label: string): TextSegment[] {
    return splitHighlight(label, this.query());
  }

  /** Setas: cima/baixo entre as linhas visíveis; direita abre, esquerda recolhe (padrão de árvore). */
  onRowKeydown(event: KeyboardEvent, node: PermissionTreeNode, hasChildren: boolean): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const rows = Array.from(
        this.host.nativeElement.querySelectorAll<HTMLElement>('.perm-tree__row[tabindex]')
      );
      const current = rows.indexOf(event.currentTarget as HTMLElement);
      const next = rows[current + (event.key === 'ArrowDown' ? 1 : -1)];
      next?.focus();
      return;
    }
    if (!hasChildren || this.expandAll()) return;
    const open = this.isExpanded(node.id);
    if ((event.key === 'ArrowRight' && !open) || (event.key === 'ArrowLeft' && open)) {
      event.preventDefault();
      this.expandToggle.emit(node.id);
    }
  }

  ariaChecked(node: PermissionTreeNode): 'true' | 'false' | 'mixed' {
    const state = this.state(node);
    return state === 'on' ? 'true' : state === 'partial' ? 'mixed' : 'false';
  }

  onRowClick(node: PermissionTreeNode): void {
    this.nodeSelect.emit(node);
  }

  onCheckClick(event: Event, node: PermissionTreeNode): void {
    event.stopPropagation();
    this.nodeToggle.emit(node);
  }

  onChevronClick(event: Event, id: string): void {
    event.stopPropagation();
    this.expandToggle.emit(id);
  }
}
